/**
 * MCP App Iframe Host — renders an MCP App in a sandboxed iframe and
 * speaks the @modelcontextprotocol/ext-apps PostMessage protocol.
 *
 * The ext-apps protocol uses JSON-RPC 2.0 messages:
 *   1. App sends  → "ui/initialize" request
 *   2. Host sends → initialize result (hostInfo, hostContext)
 *   3. App sends  → "ui/notifications/initialized" notification
 *   4. Host sends → "ui/notifications/tool-result" with the data
 *
 * The iframe is loaded via `src` (not `srcdoc`) so it gets its own CSP from
 * the server response instead of inheriting the parent page's strict CSP
 * which blocks inline scripts. Authentication is passed via `?token=` query
 * parameter since iframes can't send Authorization headers.
 *
 * PLT-4622 Phase 2 — live update channel.
 *
 *   The host can call `postLiveUpdate(deltaPayload)` (returned from this
 *   component as part of a ref-based handle) to push a fresh tool-result to
 *   the iframe without unmounting + re-mounting it. The iframe receives a
 *   second `ui/notifications/tool-result` notification — the same channel
 *   used for the initial result — and the app's `ontoolresult` handler is
 *   responsible for diffing the payload into its existing UI state.
 *
 *   The capability check is the host advertising
 *   `hostCapabilities.liveUpdates: true` on the initialize response. Cards
 *   that subscribe (today: CompanyCard via `useLiveJobStatus`) check that
 *   capability before opening the SSE connection; cards in Claude Desktop /
 *   Claude.ai see `liveUpdates: undefined` and stay on the static payload,
 *   which is the whole point of the graceful-degradation contract.
 */
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchOrThrowBlob } from '@upright/ui/api/common/fetchers/fetch-or-throw';
import { ErrorWithHttpStatus } from '@upright/ui/api/common/error-with-http-status';
import { saveAs } from '@upright/ui/common/generic/utilities/save-as';
import { useUnifiedShell } from '@upright/ui/views/unified-shell/use-unified-shell';
import { parseMcpAppDownloadRequest } from './mcp-app-download';
import { sameOriginPlatformPath } from './upright-platform-link';

const PROTOCOL_VERSION = '2026-01-26';

/**
 * How long the host waits for the iframe to complete the ext-apps handshake
 * (i.e. send `ui/notifications/initialized`) before assuming the bundle
 * failed to load or execute and surfacing a host-level error with a retry.
 *
 * PLT-4984: every loading/error/empty state of an MCP-app card lives *inside*
 * the iframe bundle, so if the bundle never loads (slow or failed fetch of the
 * `/api/chat/mcp-apps/{name}` route, a stalled request, or a crash before
 * mount) the host previously showed a bare bordered white box indefinitely,
 * with no affordance. A generous timeout keeps false positives near zero on
 * slow connections; if a late load eventually finishes the error state
 * self-heals back to `ready` (the `initialized` handler always wins).
 */
const HANDSHAKE_TIMEOUT_MS = 12_000;

/**
 * Ceiling for the auto-resized iframe height. PLT-4778: raised from 800 to
 * 1600 so long DMA cards (all categories expanded) fit without clipping;
 * content taller than this scrolls inside the iframe (`body { overflow-y:
 * auto }` in `src/ui/mcp-apps/common/styles.ts`).
 */
const IFRAME_MAX_HEIGHT_PX = 1600;

/**
 * PLT-5529: extra device-safe slack added to the height the app reports before
 * we size the iframe to it. The ext-apps SDK reports
 * `Math.ceil(documentElement.getBoundingClientRect().height)` — a **CSS-pixel**
 * ceiling of the content. On a HiDPI display (e.g. a 2x Retina MacBook) the
 * content's height snaps to the **device** pixel grid, and the sum of the
 * per-box device-snapped heights can land ~1 device pixel past that CSS-pixel
 * ceiling. The host then sizes the iframe exactly to the reported value, so the
 * inner document is ~1px too tall for its own viewport and the iframe paints a
 * phantom vertical scrollbar on a card whose content visibly fits (reported on
 * ~every card at DPR 2; invisible at DPR 1, which is why it looked flaky). Two
 * CSS px (≥ 2 device px at any DPR ≥ 1) absorbs that rounding gap. The slack is
 * transparent (the card paints its own background) so it's invisible, and it
 * only applies below the cap — content taller than the cap still clamps and
 * scrolls, so tall DMA cards are unaffected.
 */
const IFRAME_SUBPIXEL_BUFFER_PX = 2;

/**
 * The JSON-RPC messages an MCP-app iframe posts to the host. Only the fields
 * the host reads are modelled; `params` keeps an index signature because its
 * shape varies per `method` (size-changed height, send-message text, open-link
 * url). Typing the message listener as
 * `MessageEvent<HostBoundMessage>` gives `event.data` this shape directly — the
 * generic `MessageEvent` otherwise types `data` as `any`, which is what made
 * every `msg.method` / `msg.params` access an unchecked "unsafe any" read.
 */
interface HostBoundMessage {
  jsonrpc?: string;
  method?: string;
  id?: string | number | null;
  params?: {
    height?: number;
    text?: string;
    url?: string;
    [key: string]: unknown;
  };
}

/**
 * Opaque host identity advertised on initialize. PLT-4620 Phase 0 + PLT-4622
 * Phase 2: cards inside the iframe gate live-subscription behaviour on this
 * value (or, more strictly, on the boolean
 * `hostCapabilities.liveUpdates` flag). Keeping the bucket name stable
 * across releases means the gate doesn't drift when the host version
 * bumps.
 *
 * PLT-4862: the `name` is also the source of truth for the MCP-app side's
 * "is this our own chat?" check. Two consumers match on this exact string:
 *   - `INTERNAL_MCP_HOST_NAME` in `src/ui/mcp-apps/common/components.tsx`
 *     (drives the muted Open vs. Open in Upright button styling).
 *   - `HOSTS_THAT_FRAME_THE_CARD` in
 *     `src/ui/mcp-apps/common/use-external-host-border.ts` (suppresses the
 *     outer card border so we don't draw two frames around the iframe —
 *     the iframe element itself already paints one).
 * If you rename `name`, update both consumers in the same change. The
 * previous rename (`'Upright Chat'` → `'upright-web-chat'`) missed those
 * consumers and produced a double border with mismatched radii inside
 * our own chat (PLT-4862).
 */
const HOST_INFO = { name: 'upright-web-chat', version: '1.0.0' };

/**
 * UI-preview deploys live on per-commit subdomains of `uprightdb.com` (e.g.
 * `https://e161727f74b3.uprightdb.com`) served straight out of S3 + CloudFront,
 * with no ALB origin to forward `/api/*` requests to a backend. Detect this
 * host pattern so the MCP-app iframe `src` can be pointed at the same-commit
 * static bundle instead of a backend route that doesn't exist on previews.
 */
function isUiPreviewHost(): boolean {
  if (typeof window === 'undefined') return false;
  return /\.uprightdb\.com$/i.test(window.location.host);
}

interface McpAppIframeProps {
  /** MCP App bundle name, e.g. "net-impact" */
  appName: string;
  /** Tool result data to send to the app */
  toolResult: unknown;
  /** Optional fixed height; defaults to auto-resize */
  height?: number;
  /** Called when the MCP App requests sending a chat message (e.g. clicking a matter) */
  onSendMessage?: (text: string) => void;
}

/**
 * Imperative handle the parent can use to push live updates to the iframe.
 * Phase 2: `useLiveJobStatus` keeps a ref to this handle and calls
 * `postLiveUpdate` on every SSE event.
 */
export interface McpAppIframeHandle {
  /**
   * Push a delta payload to the iframe. The iframe's `ontoolresult`
   * receives the new payload and is expected to merge it into existing
   * state. No-ops when the iframe is not yet initialized — the next
   * `initialized` notification will deliver the latest `toolResult` prop
   * instead, which the host updates separately.
   */
  postLiveUpdate: (payload: unknown) => void;
}

/**
 * Renders an MCP App in a sandboxed iframe and implements the
 * ext-apps host protocol to deliver tool results.
 */
export const McpAppIframe = forwardRef<McpAppIframeHandle, McpAppIframeProps>(function McpAppIframe(
  { appName, toolResult, height, onSendMessage },
  ref,
) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const navigate = useNavigate();
  const unifiedShellEnabled = useUnifiedShell().enabled;
  const [iframeHeight, setIframeHeight] = useState(height ?? 360);
  const initializedRef = useRef(false);
  // PLT-4984: host-level load state so the card shows a skeleton (not a bare
  // white box) while the iframe bundle loads + handshakes, and a recoverable
  // error if the handshake never completes. Bumping `reloadNonce` remounts the
  // iframe (via `key`) for a fresh load attempt.
  const [loadPhase, setLoadPhase] = useState<'loading' | 'ready' | 'error'>('loading');
  const [reloadNonce, setReloadNonce] = useState(0);
  // Keep the latest toolResult in a ref so the live-update path posts the
  // freshest payload rather than the snapshot captured when the user first
  // mounted the iframe.
  const latestPayloadRef = useRef<unknown>(toolResult);
  useEffect(() => {
    latestPayloadRef.current = toolResult;
  }, [toolResult]);

  // PLT-4816: on preview hosts, load the same-commit static bundle (uploaded
  // alongside the SPA, no JWT needed — data arrives via postMessage). Other
  // envs use the backend route which serves the bundle with the relaxed
  // `MCP_APP_CSP` from `mcp-app-route.ts`.
  //
  // PLT-4720: the `?token=...` query parameter was historically added so
  // the iframe could authenticate against the bundle route (iframes can't
  // set `Authorization` headers). With the move to HttpOnly auth cookies
  // the same-origin iframe inherits the `up-access` cookie automatically,
  // so the URL is now plain. The backend route's `hapi-auth-jwt2` strategy
  // still also accepts the legacy `?token=` URL parameter (default
  // `urlKey: 'token'`), so an old SPA tab still loading the new backend
  // continues to work for one release.
  const iframeSrc = useMemo(() => {
    if (isUiPreviewHost()) {
      return `/mcp-apps/${encodeURIComponent(appName)}/index.html`;
    }
    return `/api/chat/mcp-apps/${encodeURIComponent(appName)}`;
  }, [appName]);

  /** Send any payload as `ui/notifications/tool-result`. Used on initial delivery and live updates. */
  const postToolResult = useCallback((payload: unknown) => {
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    win.postMessage(
      {
        jsonrpc: '2.0',
        method: 'ui/notifications/tool-result',
        params: {
          content: [{ type: 'text', text: JSON.stringify(payload) }],
        },
      },
      '*',
    );
  }, []);

  // Imperative handle — exposes `postLiveUpdate` to the parent.
  useImperativeHandle(
    ref,
    () => ({
      postLiveUpdate: payload => {
        // Kept in the ref even before `initialized`, and only posted after it:
        // the initial delivery sends the ref, so an update that lands first
        // still wins over the stored payload. A card remounted after its job
        // finished (a reopened chat, or one re-seeded by an injected message)
        // gets its terminal snapshot before the handshake, and the live hook
        // stops at a terminal state, so a dropped one is never sent again and
        // the card stays on "Computing…" (PLT-7428).
        latestPayloadRef.current = payload;
        if (!initializedRef.current) return;
        postToolResult(payload);
      },
    }),
    [postToolResult],
  );

  // Handle PostMessage from the iframe (ext-apps protocol)
  const handleMessage = useCallback(
    (event: MessageEvent<HostBoundMessage | null>) => {
      const iframe = iframeRef.current;
      if (!iframe?.contentWindow || event.source !== iframe.contentWindow) return;

      const msg = event.data;
      if (!msg || msg.jsonrpc !== '2.0') return;

      // App sends "ui/initialize" request
      if (msg.method === 'ui/initialize' && msg.id != null) {
        iframe.contentWindow.postMessage(
          {
            jsonrpc: '2.0',
            id: msg.id,
            result: {
              protocolVersion: PROTOCOL_VERSION,
              hostInfo: HOST_INFO,
              // Advertise host capabilities so cards inside the iframe
              // can decide whether to use a richer interaction. Cards
              // running in Claude Desktop / Claude.ai don't see these
              // flags and fall back to their static-payload behaviour.
              //   - liveUpdates  (PLT-4622)   — host can push deltas via
              //     `postLiveUpdate` without remounting the iframe.
              //   - downloadFile (PLT-7593)   — the standard MCP Apps capability:
              //     `ui/download-file` below saves the chat's PDF exports.
              hostCapabilities: { liveUpdates: true, downloadFile: {} },
              hostContext: {
                theme: 'light',
                displayMode: 'inline',
              },
            },
          },
          '*',
        );
        return;
      }

      // App sends "ui/notifications/initialized" — now deliver the tool result
      if (msg.method === 'ui/notifications/initialized' && !initializedRef.current) {
        initializedRef.current = true;
        // The bundle loaded and the handshake completed — the iframe now
        // paints its own content (or its own loading/error/empty state), so
        // drop the host-level skeleton/error overlay (PLT-4984).
        setLoadPhase('ready');
        // Use the ref so any live-update that arrived before `initialized`
        // wins over the original prop value.
        postToolResult(latestPayloadRef.current);
        return;
      }

      // App sends "ui/notifications/size-changed" — auto-resize. Add a small
      // sub-pixel buffer (PLT-5529) before clamping to the cap, so HiDPI
      // device-pixel rounding can't leave the content ~1px taller than the
      // iframe and paint a phantom scrollbar. See the constants above.
      if (msg.method === 'ui/notifications/size-changed' && msg.params?.height) {
        const reported = msg.params.height;
        setIframeHeight(Math.min(reported + IFRAME_SUBPIXEL_BUFFER_PX, IFRAME_MAX_HEIGHT_PX));
        return;
      }

      // App sends "ui/notifications/send-message" — inject user message into chat
      if (msg.method === 'ui/notifications/send-message' && msg.params?.text) {
        onSendMessage?.(msg.params.text);
        return;
      }

      // App sends "ui/open-link". In the unified shell a page of this app opens in the same tab,
      // so its Back returns to the chat (PLT-7159); anything else opens in a new tab.
      if (msg.method === 'ui/open-link' && msg.params?.url && msg.id != null) {
        const inAppPath = unifiedShellEnabled ? sameOriginPlatformPath(msg.params.url) : null;
        if (inAppPath !== null) {
          navigate(inAppPath);
        } else {
          window.open(msg.params.url, '_blank', 'noopener');
        }
        iframe.contentWindow.postMessage({ jsonrpc: '2.0', id: msg.id, result: {} }, '*');
        return;
      }

      // App sends "ui/download-file" (PLT-7593). The card cannot fetch, so the host fetches the
      // export with the user's session and saves it; `parseMcpAppDownloadRequest` limits what a
      // card can make it fetch. The answer says whether the file was saved.
      if (msg.method === 'ui/download-file' && msg.id != null) {
        const requestId = msg.id;
        const cardWindow = iframe.contentWindow;
        const reply = (result: { isError?: boolean }) => {
          cardWindow.postMessage({ jsonrpc: '2.0', id: requestId, result }, '*');
        };
        const download = parseMcpAppDownloadRequest(msg.params);
        if (download === null) {
          reply({ isError: true });
          return;
        }
        // The card can arrive just before its chat turn is persisted. Retry that missing-report
        // response once; access checks still run on both attempts, and other failures are final.
        void fetchOrThrowBlob(download.path, 'POST')
          .catch(async (error: unknown) => {
            if (!(error instanceof ErrorWithHttpStatus) || error.status !== 410) throw error;
            await new Promise(resolve => window.setTimeout(resolve, 1500));
            return await fetchOrThrowBlob(download.path, 'POST');
          })
          .then(
            blob => {
              saveAs(blob, download.fileName);
              reply({});
            },
            () => {
              reply({ isError: true });
            },
          );
        return;
      }
    },
    [onSendMessage, postToolResult, unifiedShellEnabled, navigate],
  );

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [handleMessage]);

  // PLT-4984: reset to the loading state and arm the handshake watchdog
  // whenever the iframe (re)mounts — on first mount, when `appName` changes
  // the src, or after a manual reload (`reloadNonce`). If `initialized` never
  // arrives within the timeout the bundle failed to load/execute; flip to the
  // error overlay so the card never sits as an indefinite blank box.
  useEffect(() => {
    initializedRef.current = false;
    setLoadPhase('loading');
    const timer = window.setTimeout(() => {
      if (!initializedRef.current) setLoadPhase('error');
    }, HANDSHAKE_TIMEOUT_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [iframeSrc, reloadNonce]);

  // Remounts the iframe (via the `key` below) so the bundle is fetched and the
  // ext-apps handshake re-attempted from scratch.
  const handleReload = useCallback(() => {
    setReloadNonce(n => n + 1);
  }, []);

  // The iframe is always mounted (so it actually loads), with the host-level
  // loading skeleton / error overlay painted on top until the handshake
  // completes. `key={reloadNonce}` lets `handleReload` force a fresh load.
  return (
    <div className="mcp-app-iframe-host" style={{ position: 'relative', marginBottom: 'var(--space__sm)' }}>
      {/*
       * The frame (border + radius + white background) lives in the
       * `.mcp-app-iframe-frame` CSS class rather than inline so the
       * collapsible-card wrapper can square the iframe's top corners and
       * drop its top border when the card is expanded, joining the iframe
       * flush to the toggle row above it as a single card (PLT-4997). Only
       * the dynamic auto-resize `height` stays inline.
       */}
      <iframe
        key={reloadNonce}
        ref={iframeRef}
        src={iframeSrc}
        // PLT-5166: `allow-downloads` lets app cards trigger user-initiated
        // file saves (e.g. the climate-risk-map "Export PNG" button, which
        // composes a canvas client-side and clicks an <a download>). Without
        // it Chrome silently blocks downloads from sandboxed iframes. It does
        // NOT allow navigation, popups, or automatic downloads-on-load.
        sandbox="allow-scripts allow-same-origin allow-downloads"
        className="mcp-app-iframe-frame"
        style={{ height: iframeHeight }}
        title={`MCP App: ${appName}`}
      />
      {loadPhase === 'loading' && (
        <div className="mcp-app-iframe-overlay mcp-app-iframe-overlay--loading" aria-busy="true" aria-live="polite">
          <div className="mcp-app-loading-bar" />
          <div className="mcp-app-loading-bar mcp-app-loading-bar--short" />
          <div className="mcp-app-loading-bar" />
        </div>
      )}
      {loadPhase === 'error' && (
        <div className="mcp-app-iframe-overlay mcp-app-iframe-overlay--error" role="alert">
          <p className="mcp-app-iframe-overlay-text">This card couldn’t load.</p>
          <button type="button" className="mcp-app-iframe-overlay-retry" onClick={handleReload}>
            Reload
          </button>
        </div>
      )}
    </div>
  );
});
